/**
 * Social Authentication Service
 * Handles OAuth authentication for social networks (Facebook, LinkedIn, Twitter)
 */

import httpService from '../httpService.js'
import Vue from 'vue'

class SocialAuthService {
  /**
   * Get Facebook user profile information
   * @param {string} accessToken - Facebook access token
   * @returns {Promise<Object>} User profile data
   */
  async getFacebookUserInfo(accessToken) {
    const FB_URL_PROFILE =
      'https://graph.facebook.com/me?fields=id,first_name,last_name,email,picture,locale,location,timezone&access_token='
    
    try {
      // Direct HTTP call (not through httpService to avoid baseURL prepending)
      const response = await Vue.http.get(`${FB_URL_PROFILE}${accessToken}`)
      return { success: true, data: response.body, response }
    } catch (error) {
      return { success: false, error: true, data: error.body, originalError: error }
    }
  }

  /**
   * Get Facebook long-lived access token
   * @param {string} shortToken - Short-lived access token
   * @returns {Promise<Object>} Long-lived token
   */
  async getFacebookLongLivedToken(shortToken) {
    const { VUE_APP_FB_ID, VUE_APP_FB_SECRET } = process.env
    const FB_URL_TOKEN = `https://graph.facebook.com/oauth/access_token?client_id=${VUE_APP_FB_ID}&client_secret=${VUE_APP_FB_SECRET}&grant_type=fb_exchange_token&fb_exchange_token=`

    try {
      // Direct HTTP call (not through httpService to avoid baseURL prepending)
      const response = await Vue.http.get(`${FB_URL_TOKEN}${shortToken}`)
      return { success: true, data: response.body, response }
    } catch (error) {
      return { success: false, error: true, data: error.body, originalError: error }
    }
  }

  /**
   * Get Facebook pages for user
   * @param {string} accessToken - Facebook access token
   * @returns {Promise<Object>} User pages data
   */
  async getFacebookPages(accessToken) {
    const FB_URL_PAGES =
      'https://graph.facebook.com/me/accounts?limit=100&fields=id,name,link,access_token,picture,location,parent_page&access_token='
    
    try {
      // Direct HTTP call (not through httpService to avoid baseURL prepending)
      const response = await Vue.http.get(`${FB_URL_PAGES}${accessToken}`)
      return { success: true, data: response.body, response }
    } catch (error) {
      return { success: false, error: true, data: error.body, originalError: error }
    }
  }

  /**
   * Verify Facebook authentication
   * @param {Object} facebookData - Facebook authentication data
   * @param {string} facebookData.id - Facebook user ID
   * @param {string} facebookData.token - Facebook access token
   * @returns {Promise<Object>} Authentication response
   */
  async verifyFacebook(facebookData) {
    const response = await httpService.post('/auth/facebook', facebookData)
    return response
  }

  /**
   * Verify LinkedIn OAuth callback (existing endpoint)
   * @param {Object} linkedinData - LinkedIn OAuth callback data
   * @param {string} linkedinData.code - LinkedIn authorization code
   * @returns {Promise<Object>} Authentication response
   */
  async verifyLinkedin(linkedinData) {
    const { code } = linkedinData

    // Use existing endpoint from Social mixin
    const response = await httpService.post('/auth/linkedin/signed', { code })
    return response
  }

  /**
   * Initialize Facebook SDK
   * @param {string} appId - Facebook App ID
   * @param {string} version - Facebook API version
   * @returns {Promise<void>}
   */
  initializeFacebookSDK(appId, version = 'v18.0') {
    return new Promise(resolve => {
      if (window.FB) {
        resolve()
        return
      }

      window.fbAsyncInit = function() {
        window.FB.init({
          appId: appId,
          cookie: true,
          xfbml: true,
          version: version
        })
        resolve()
      }

      // Load Facebook SDK
      const script = document.createElement('script')
      script.async = true
      script.defer = true
      script.crossOrigin = 'anonymous'
      script.src = 'https://connect.facebook.net/en_US/sdk.js'
      document.head.appendChild(script)
    })
  }

  /**
   * Facebook Login
   * @param {Object} options - Login options
   * @param {Array} options.scope - Permissions scope
   * @returns {Promise<Object>} Facebook login response
   */
  async facebookLogin(options = {}) {
    const { scope = ['email', 'public_profile'] } = options

    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('Facebook SDK not loaded'))
        return
      }

      window.FB.login(
        response => {
          if (response.authResponse) {
            // Get user profile
            window.FB.api(
              '/me',
              { fields: 'name,first_name,last_name,email,picture' },
              profile => {
                resolve({
                  accessToken: response.authResponse.accessToken,
                  userID: response.authResponse.userID,
                  profile
                })
              }
            )
          } else {
            reject(new Error('Facebook login cancelled or failed'))
          }
        },
        { scope: scope.join(',') }
      )
    })
  }
}

export default new SocialAuthService()
